const DURATION_UNITS = new Set(['s', 'second', 'seconds', 'segundo', 'segundos']);
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/u;
const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 2,
});

type AuraDateTableValue = {
    value?: unknown;
    date?: unknown;
    startedAt?: unknown;
    started_at?: unknown;
};

const normalizeUnit = (unit?: string): string => unit?.trim().toLowerCase() || '';

export const isAuraDurationUnit = (unit?: string): boolean =>
    DURATION_UNITS.has(normalizeUnit(unit));

export const formatAuraDateLabel = (label: string): string => {
    const match = ISO_DATE_PATTERN.exec(label.trim());
    if (!match) return label;

    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
};

export const formatAuraDuration = (seconds: number): string => {
    if (!Number.isFinite(seconds)) return '-';

    const normalizedSeconds = Math.max(0, seconds);
    if (normalizedSeconds < 60) {
        return `${NUMBER_FORMATTER.format(normalizedSeconds)} s`;
    }

    const totalSeconds = Math.round(normalizedSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    return [hours, minutes, remainingSeconds]
        .map((value) => String(value).padStart(2, '0'))
        .join(':');
};

export const formatAuraMetric = (value: unknown, unit?: string): string => {
    const numericValue = typeof value === 'number' ? value : Number.NaN;
    if (!Number.isFinite(numericValue)) return String(value ?? '-');
    if (isAuraDurationUnit(unit)) return formatAuraDuration(numericValue);

    const formattedValue = NUMBER_FORMATTER.format(numericValue);
    const displayUnit = unit?.trim();
    if (!displayUnit) return formattedValue;

    return `${formattedValue}${displayUnit === '%' ? '' : ' '}${displayUnit}`;
};

const readAuraDateTableValue = (value: unknown): string | null => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (value instanceof Date) return value.toISOString();
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const dateValue = value as AuraDateTableValue;
    const candidate =
        dateValue.value ?? dateValue.date ?? dateValue.startedAt ?? dateValue.started_at ?? null;

    return typeof candidate === 'string' || typeof candidate === 'number'
        ? String(candidate)
        : null;
};

export const formatAuraStructuredTableValue = (column: string, value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';

    if (column === 'data') {
        const dateValue = readAuraDateTableValue(value);
        return dateValue ? formatAuraDateLabel(dateValue) : '-';
    }

    return String(value);
};
